if (!customElements.get('tabbed-featured-collection')) {
  class TabbedFeaturedCollection extends HTMLElement {
    constructor() {
      super();
      this.tabButtons = Array.from(this.querySelectorAll('.tabbed-collection__tab-btn'));
      this.panels = Array.from(this.querySelectorAll('.tabbed-collection__panel'));
      this.indicator = this.querySelector('.tabbed-collection__tab-indicator');
      this.tabsWrapper = this.querySelector('.tabbed-collection__tabs-wrapper');

      this.initTabs();
      this.initSliders();

      window.addEventListener('resize', this.onResize.bind(this));
    }

    connectedCallback() {
      // Delay indicator position set slightly to allow layout calculations
      requestAnimationFrame(() => {
        this.updateIndicatorPosition(this.getActiveTabButton());
        this.updateAllSliders();
      });
    }

    getActiveTabButton() {
      return this.tabButtons.find(btn => btn.classList.contains('tabbed-collection__tab-btn--active')) || this.tabButtons[0];
    }

    initTabs() {
      this.tabButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const targetBlockId = button.getAttribute('data-tab');
          this.switchTab(button, targetBlockId);
        });
      });
    }

    switchTab(selectedBtn, targetBlockId) {
      if (!selectedBtn || selectedBtn.classList.contains('tabbed-collection__tab-btn--active')) {
        return;
      }

      // Update Tab Buttons
      this.tabButtons.forEach(btn => {
        const isActive = btn === selectedBtn;
        btn.classList.toggle('tabbed-collection__tab-btn--active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      // Update Indicator
      this.updateIndicatorPosition(selectedBtn);

      // Update Panels
      this.panels.forEach(panel => {
        const isMatch = panel.getAttribute('data-panel') === targetBlockId;
        if (isMatch) {
          panel.classList.add('tabbed-collection__panel--active');
          panel.setAttribute('aria-hidden', 'false');

          // Reset slider scroll position & recalculate buttons smoothly
          const track = panel.querySelector('.tabbed-collection__slider-track');
          if (track) {
            track.scrollLeft = 0;
            requestAnimationFrame(() => {
              this.updateSliderButtons(panel);
            });
          }
        } else {
          panel.classList.remove('tabbed-collection__panel--active');
          panel.setAttribute('aria-hidden', 'true');
        }
      });
    }

    updateIndicatorPosition(activeBtn) {
      if (!activeBtn || !this.indicator || !this.tabsWrapper) return;
      const btnRect = activeBtn.getBoundingClientRect();
      const wrapperRect = this.tabsWrapper.getBoundingClientRect();

      const left = btnRect.left - wrapperRect.left + this.tabsWrapper.scrollLeft;
      const width = btnRect.width;

      this.indicator.style.left = `${left}px`;
      this.indicator.style.width = `${width}px`;
    }

    initSliders() {
      this.panels.forEach(panel => {
        const track = panel.querySelector('.tabbed-collection__slider-track');
        const prevBtn = panel.querySelector('.tabbed-collection__arrow--prev');
        const nextBtn = panel.querySelector('.tabbed-collection__arrow--next');

        if (!track) return;

        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const step = this.getScrollStep(track);
            track.scrollBy({ left: -step, behavior: 'smooth' });
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const step = this.getScrollStep(track);
            track.scrollBy({ left: step, behavior: 'smooth' });
          });
        }

        track.addEventListener('scroll', () => {
          this.updateSliderButtons(panel);
        }, { passive: true });

        // Initial setup for buttons
        this.updateSliderButtons(panel);
      });
    }

    getScrollStep(track) {
      const firstSlide = track.querySelector('.tabbed-collection__slide');
      if (firstSlide) {
        const gap = parseInt(window.getComputedStyle(track).gap) || 20;
        return (firstSlide.offsetWidth + gap) * 2;
      }
      return track.clientWidth * 0.75;
    }

    updateSliderButtons(panel) {
      const track = panel.querySelector('.tabbed-collection__slider-track');
      const prevBtn = panel.querySelector('.tabbed-collection__arrow--prev');
      const nextBtn = panel.querySelector('.tabbed-collection__arrow--next');

      if (!track) return;

      const maxScrollLeft = track.scrollWidth - track.clientWidth - 2; // threshold for subpixel

      if (prevBtn) {
        if (track.scrollLeft <= 2) {
          prevBtn.setAttribute('disabled', 'disabled');
        } else {
          prevBtn.removeAttribute('disabled');
        }
      }

      if (nextBtn) {
        if (track.scrollLeft >= maxScrollLeft) {
          nextBtn.setAttribute('disabled', 'disabled');
        } else {
          nextBtn.removeAttribute('disabled');
        }
      }
    }

    updateAllSliders() {
      this.panels.forEach(panel => {
        this.updateSliderButtons(panel);
      });
    }

    onResize() {
      this.updateIndicatorPosition(this.getActiveTabButton());
      this.updateAllSliders();
    }
  }

  customElements.define('tabbed-featured-collection', TabbedFeaturedCollection);
}
